// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { selectCollectingRouteSites } from 'redux/slices/collectingRoute';
import { Path } from 'routes';
import { getApiPath, mergeClasses, normalizeNumber } from 'util/commons';
import styles from './CollectingSiteSelectionForm.module.scss';
import { IconMap } from 'components/atoms/icons/IconMap';
import CollectRouteSummary from 'components/molecules/CollectRouteSummary';
import CollectRouteSelectStep from 'components/molecules/CollectRouteSelectStep';
import { IconEyeSlash } from 'components/atoms/icons/IconEyeSlash';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BigNumber from 'bignumber.js';
import { IconCheckCircle } from 'components/atoms/icons/IconCheckCircle';
import { useGetWindowSize } from 'util/hooks/useGetWindowSize';
import { LAYOUT_BREAK_POINTS } from 'util/ConstantValues';
import { useCheckRouteCollectProcess } from 'util/hooks/useCheckRouteCollectProcess';
import { IconSearch } from 'components/atoms/icons/IconSearch';
import CollectOffSitesSearchModal from '../CollectOffSitesSearchModal';
import { CollectingRouteSession } from 'util/collectingRoute';

const CollectingSiteSelectionForm: React.FC = () => {
  const collectingRouteSites = useSelector(selectCollectingRouteSites);
  const { routeId } = useParams<{ routeId: string }>();
  const history = useHistory();
  const { windowWidth } = useGetWindowSize();
  const { t } = useTranslation();
  const [showOffSitesSearch, setShowOffSitesSearch] = useState(false);

  const { activeId, processedSites, displayingSites } =
    useCheckRouteCollectProcess();

  const canCompleteRoute = useMemo(() => {
    return displayingSites.every((site) => processedSites.includes(site.id));
  }, [displayingSites, processedSites]);

  const summary = useMemo(() => {
    return displayingSites
      .filter(
        (site) => !site.enableSubstituteRegist && Number(site.weight) !== 0
      )
      .reduce(
        (sum, item) => {
          sum.quantity = sum.quantity.plus(item.quantity || 0);
          sum.weight = sum.weight.plus(item.weight || 0);
          return sum;
        },
        {
          quantity: new BigNumber(0),
          weight: new BigNumber(0),
        }
      );
  }, [displayingSites]);

  const handleGoToEdit = () => {
    history.push(
      `${getApiPath(Path.routeDetail, { routeId })}${window.location.search}`,
      {
        mode: 'edit',
      }
    );
  };

  const handleCompleteRoute = () => {
    if (!canCompleteRoute) {
      return;
    }

    CollectingRouteSession.remove(routeId);

    history.push(`${Path.routeList}${window.location.search}`);
    toast.success(t('messages.M_route_collect_completed'));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <IconMap width={32} height={32} color="#86ea5c" />
          <p>
            {collectingRouteSites?.routeName ??
              t('collect_site_selection.title')}
          </p>
        </div>

        <CollectRouteSummary
          weight={summary.weight}
          quantity={summary.quantity}
        />
      </div>

      <div className={styles.infoBarContainer}>
        <div className={styles.infoBar} onClick={handleGoToEdit}>
          <IconEyeSlash />
          {t('collect_site_selection.collected_count', {
            count: normalizeNumber({
              value: collectingRouteSites
                ? collectingRouteSites?.sites.length - displayingSites.length
                : 0,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any,
          })}
        </div>

        <div
          className={mergeClasses(styles.infoBar, styles.searchInfoBar)}
          onClick={() => setShowOffSitesSearch(true)}
        >
          <IconSearch color="black" size={24} />
          {t('confirm_select_route_item.off_sites_search')}
        </div>
      </div>

      <CollectRouteSelectStep
        sites={displayingSites}
        activeId={activeId}
        processedSiteIds={processedSites}
      />

      <div className={styles.footer}>
        <button className={styles.editButton} onClick={handleGoToEdit}>
          {t('common.edit')}
        </button>

        <button
          className={mergeClasses(styles.completeButton, {
            [styles.disabledButton]: !canCompleteRoute,
          })}
          onClick={handleCompleteRoute}
        >
          <span>{t('collect_site_selection.complete_route')}</span>
          <IconCheckCircle
            size={windowWidth <= LAYOUT_BREAK_POINTS.MD ? 24 : 32}
          />
        </button>
      </div>

      {showOffSitesSearch && (
        <CollectOffSitesSearchModal
          onClose={() => setShowOffSitesSearch(false)}
        />
      )}
    </div>
  );
};

export default CollectingSiteSelectionForm;
